// src/services/hintService.ts
/**
 * Hint Service
 *
 * Provides cryptic hints about possible element combinations
 * without revealing the exact recipe
 */

import { ELEMENT_DEFS } from '../components/elementDefinitions';
import { ElementDef } from '../store/elementStore';

export interface Hint {
  message: string;
  ingredients: string[]; // The actual elements needed (not shown to user)
  result: string; // The result element ID
  tier: number;
}

/**
 * Get hints for undiscovered elements based on currently unlocked elements
 */
export const getHintsForUnlocked = (unlockedIds: string[]): Hint[] => {
  const unlocked = new Set(unlockedIds);
  const hints: Hint[] = [];

  // Find all undiscovered elements that can be made with unlocked ingredients
  Object.values(ELEMENT_DEFS).forEach((element) => {
    if (element.recipe && element.recipe.length > 0) {
      // Check if result is already unlocked
      if (unlocked.has(element.id)) return;

      // Check if all required ingredients are unlocked
      const allIngredientsUnlocked = element.recipe.every((ing) => unlocked.has(ing));
      if (allIngredientsUnlocked) {
        const hint = generateHintMessage(element);
        hints.push({
          message: hint,
          ingredients: element.recipe,
          result: element.id,
          tier: element.tier,
        });
      }
    }
  });

  // Sort by tier (lower tiers first)
  return hints.sort((a, b) => a.tier - b.tier);
};

/**
 * Get a random hint from available hints
 */
export const getRandomHint = (unlockedIds: string[]): Hint | null => {
  const hints = getHintsForUnlocked(unlockedIds);
  if (hints.length === 0) return null;
  return hints[Math.floor(Math.random() * hints.length)];
};

/**
 * Get hints for a specific tier
 */
export const getHintsForTier = (unlockedIds: string[], tier: number): Hint[] => {
  const allHints = getHintsForUnlocked(unlockedIds);
  return allHints.filter((hint) => hint.tier === tier);
};

/**
 * Generate a cryptic hint message for an element
 */
function generateHintMessage(element: ElementDef): string {
  const hintTemplates: Record<string, string[]> = {
    // Tier 2 hints
    steam: ['When heat meets water, something rises...', 'Vapor forms when opposites clash'],
    lava: ['Earth and flame unite in molten fury', 'When ground meets inferno...'],
    mud: ['Mix the solid with the liquid...', 'Earth drinks water and becomes...'],
    smoke: ['Fire breathes in the air and exhales...', 'What rises from burning?'],
    cloud: ['Air carries water to the sky...', 'Vapor gathers in the heavens'],
    ice_storm: ['When frost meets tempest...', 'A frozen hurricane forms from...'],
    frost: ['Cold wind leaves its mark on surfaces...', 'When ice touches the breeze...'],
    thunder: ['Sky-born electricity rumbles through clouds...', 'The voice of lightning is...'],
    eclipse: ['When illumination meets shadow...', 'Light and darkness align to create...'],
    wind: ['Air multiplied becomes a force...', 'When breeze meets breeze...'],

    // Tier 3 hints
    volcano: ['Molten earth meets open sky...', 'Lava seeks the heavens'],
    storm: ['Air and water clash violently...', 'When wind meets rain...'],
    obsidian: ['Rapid cooling transforms molten stone...', 'Water quenches volcanic fury'],
    blizzard: ['Ice storm rides the howling wind...', 'Frozen tempest combines with gale'],
    tornado: ['Wind spirals with storm energy...', 'When gale meets tempest...'],
    earthquake: ['Ground trembles with power unleashed...', 'Earth vibrates with energy...'],
    cave: ['Darkness hollows out the peak...', 'Shadow carves into mountain...'],
    beach: ['Ocean kisses golden grains...', 'Where waves meet sand...'],
    prism: ['Glass bends light into colors...', 'Transparency fragments illumination...'],

    // Tier 4 hints
    dragon: ['The reborn flame takes reptilian form...', 'Phoenix meets lizard in legend'],
    phoenix: ['Life embraces eternal flame...', 'Immortal fire takes living form'],
    beast: ['Life darkened by shadow becomes fierce...', 'Darkness corrupts life into...'],
    whale: ['Fish grows massive in vast waters...', 'Ocean depths birth giants from...'],
    honey: ['Flying flowers create golden sweetness...', 'Bee and bloom produce...'],
    mushroom: ['Plants grow in places light fears...', 'Flora thrives in shadow'],

    // Tier 5 hints
    bread: ['Grain kissed by fire becomes sustenance...', 'Wheat transforms through heat'],
    cheese: ['Milk matures with patience...', 'Time ages the dairy...'],
    civilization: ['Humanity multiplies into society...', 'One becomes many, then becomes...'],
    chemistry: ['Wisdom meets corrosion to understand...', 'Knowledge dissolves in acid to reveal...'],

    // Tier 6 hints
    train: ['Mechanism pulls the old cart forward...', 'Machine upgrades transportation'],
    computer: ['Electricity gains logical thought...', 'Power meets reason to calculate'],
    internet: ['Computers link to share knowledge...', 'Two thinking machines connect'],
    robot: ['Machine gains computational mind...', 'Computer inhabits mechanical body'],
    ai: ['Computer absorbs all wisdom...', 'Knowledge merges with calculation'],

    // Tier 7 hints
    universe: ['Cosmic explosion fills the void...', 'Big Bang expands into space'],
    singularity: ['Black hole consumes time itself...', 'Gravity well devours temporal flow'],
    transcendence: ['Aware mind comprehends all existence...', 'Consciousness embraces the universe'],
    antimatter: ['Void inverses substance...', 'Matter meets its opposite'],
    teleportation: ['Quantum thought bends space...', 'Quantum computer masters distance'],
  };

  // Check if we have specific hints for this element
  if (hintTemplates[element.id]) {
    const templates = hintTemplates[element.id];
    return templates[Math.floor(Math.random() * templates.length)];
  }

  // Generate generic hints based on ingredients
  const ingredientNames = element.recipe.map((id) => {
    const ing = ELEMENT_DEFS[id];
    return ing ? ing.name : id;
  });

  // Generic hint templates
  const genericTemplates = [
    `Combine the essence of ${getVagueCategory(element.recipe[0])} with ${getVagueCategory(element.recipe[1])}...`,
    `What happens when ${getHintWord(element.recipe[0])} meets ${getHintWord(element.recipe[1])}?`,
    `Merge ${getElementProperty(element.recipe[0])} and ${getElementProperty(element.recipe[1])} to discover...`,
    `The union of ${getVagueCategory(element.recipe[0])} and ${getVagueCategory(element.recipe[1])} yields...`,
  ];

  if (element.recipe.length === 3) {
    return `Three elements combine: ${getVagueCategory(element.recipe[0])}, ${getVagueCategory(element.recipe[1])}, and ${getVagueCategory(element.recipe[2])}...`;
  } else if (element.recipe.length === 4) {
    return `Four essences merge: Try combinations with ${getVagueCategory(element.recipe[0])} and others...`;
  }

  return genericTemplates[Math.floor(Math.random() * genericTemplates.length)];
}

/**
 * Get a vague category hint for an element
 */
function getVagueCategory(elementId: string): string {
  const element = ELEMENT_DEFS[elementId];
  if (!element) return 'the unknown';

  const categories: Record<string, string> = {
    fire: 'heat', water: 'fluid', earth: 'solid', air: 'gas',
    light: 'radiance', dark: 'shadow', life: 'vitality', aether: 'essence',
    metal: 'mineral', ice: 'cold',
    steam: 'vapor', lava: 'molten rock', cloud: 'mist', storm: 'tempest',
    human: 'humanity', machine: 'mechanism', electricity: 'power',
    knowledge: 'wisdom', time: 'temporal flow', space: 'void',
  };

  return categories[elementId] || 'something';
}

/**
 * Get a hint word for an element (more poetic)
 */
function getHintWord(elementId: string): string {
  const element = ELEMENT_DEFS[elementId];
  if (!element) return 'the mysterious';

  const hintWords: Record<string, string> = {
    fire: 'flame', water: 'tide', earth: 'soil', air: 'breeze',
    light: 'illumination', dark: 'darkness', life: 'vitality', aether: 'spirit',
    metal: 'ore', ice: 'frost',
    storm: 'tempest', ocean: 'depths', mountain: 'peak', forest: 'woods',
  };

  return hintWords[elementId] || element.name.toLowerCase();
}

/**
 * Get a property description for an element
 */
function getElementProperty(elementId: string): string {
  const element = ELEMENT_DEFS[elementId];
  if (!element) return 'the unknown';

  const properties: Record<string, string> = {
    fire: 'burning essence', water: 'flowing nature', earth: 'solid foundation',
    air: 'ethereal breath', light: 'brilliant glow', dark: 'deep shadow',
    life: 'living force', aether: 'mystical energy', metal: 'rigid form',
    ice: 'frozen touch',
  };

  return properties[elementId] || `the ${element.name.toLowerCase()}`;
}

/**
 * Get count of available hints
 */
export const getHintCount = (unlockedIds: string[]): number => {
  return getHintsForUnlocked(unlockedIds).length;
};

/**
 * Check if a specific combination would work (without revealing the result)
 */
export const canCombine = (ingredients: string[]): boolean => {
  const sortedKey = [...ingredients].sort().join(',');

  for (const element of Object.values(ELEMENT_DEFS)) {
    if (element.recipe && element.recipe.length > 0) {
      const recipeKey = [...element.recipe].sort().join(',');
      if (recipeKey === sortedKey) {
        return true;
      }
    }
  }

  return false;
};

// ===========================
// Hint Credit System
// ===========================

interface HintCredits {
  credits: number;
  lastResetDate: string;
  dailyHintsUsed: number;
  totalHintsUsed: number;
}

const STORAGE_KEY = 'alchemist_hint_credits';
const DAILY_FREE_HINTS = 3;
const AD_REWARD_HINTS = 5;

/**
 * Get current hint credits
 */
export const getHintCredits = (): HintCredits => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const credits: HintCredits = JSON.parse(stored);

      // Check if we need to reset daily hints
      const today = new Date().toDateString();
      if (credits.lastResetDate !== today) {
        credits.dailyHintsUsed = 0;
        credits.lastResetDate = today;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(credits));
      }

      return credits;
    }
  } catch (e) {
    console.error('Error reading hint credits:', e);
  }

  // Initialize default credits
  const defaultCredits: HintCredits = {
    credits: 10, // Start with 10 bonus hints
    lastResetDate: new Date().toDateString(),
    dailyHintsUsed: 0,
    totalHintsUsed: 0,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultCredits));
  return defaultCredits;
};

/**
 * Check if user has any hints available (daily or credits)
 */
export const hasHintsAvailable = (): boolean => {
  const credits = getHintCredits();
  return credits.dailyHintsUsed < DAILY_FREE_HINTS || credits.credits > 0;
};

/**
 * Get total available hints (daily + credits)
 */
export const getTotalHintsAvailable = (): number => {
  const credits = getHintCredits();
  const dailyRemaining = Math.max(0, DAILY_FREE_HINTS - credits.dailyHintsUsed);
  return dailyRemaining + credits.credits;
};

/**
 * Consume a hint (deducts from daily or credits)
 */
export const consumeHint = (): boolean => {
  const credits = getHintCredits();

  // Try to use daily hint first
  if (credits.dailyHintsUsed < DAILY_FREE_HINTS) {
    credits.dailyHintsUsed++;
    credits.totalHintsUsed++;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(credits));
    return true;
  }

  // Otherwise use bonus credits
  if (credits.credits > 0) {
    credits.credits--;
    credits.totalHintsUsed++;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(credits));
    return true;
  }

  return false;
};

/**
 * Add hint credits (for watching ads or other rewards)
 */
export const addHintCredits = (amount: number): void => {
  const credits = getHintCredits();
  credits.credits += amount;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(credits));
};

/**
 * Reward hints for watching an ad (placeholder for ad integration)
 */
export const rewardHintsForAd = (): number => {
  addHintCredits(AD_REWARD_HINTS);
  return AD_REWARD_HINTS;
};

/**
 * Get hint credit breakdown
 */
export const getHintCreditBreakdown = () => {
  const credits = getHintCredits();
  const dailyRemaining = Math.max(0, DAILY_FREE_HINTS - credits.dailyHintsUsed);

  return {
    dailyFree: dailyRemaining,
    bonusCredits: credits.credits,
    total: dailyRemaining + credits.credits,
    totalUsed: credits.totalHintsUsed,
  };
};
